/**
 * useStoreDocument
 *
 * Hook for subscribing to a Firestore document store.
 * Returns state (Valtio proxy) and persist (Firestore API).
 *
 * @example
 * const { state, persist } = useStoreDocument<SchemaData>('schema');
 * const snap = useSnapshot(state);
 * if (snap.loading) return <Spinner />;
 * // Read (from snapshot)
 * const title = snap.data?.title;
 * // Write (direct Valtio mutation)
 * state.data.title = 'New Title';
 * // Save to Firebase
 * persist.update({ title: 'New Title' });
 *
 * // `modifiedAt` is maintained by the store persistence layer.
 */

import { useContext } from 'react';
import { useSnapshot } from 'valtio';
import { StoresContext } from './core/StoresContext';
import { getStoreFromContext } from './core/getStoreFromContext';
import type { DocumentState, DocumentPersist } from './Store.types';

type UseStoreDocumentResult<T> = {
  /** Mutable Valtio state for optimistic updates. */
  state: DocumentState<T>;
  /** Firestore persistence API for document writes. */
  persist: DocumentPersist<T>;
  /** Valtio useSnapshot, use to subscribe to state changes, mutate state directly. */
  useSnapshot: typeof useSnapshot;
};

/**
 * Get document state and persist from the store.
 * Returns state (Valtio proxy) and persist (Firestore API).
 * Call `useSnapshot(state)` in the component for reactivity.
 *
 * @param storeName - Name of the document store
 */
export function useStoreDocument<T extends Record<string, unknown>>(
  storeName: string
): UseStoreDocumentResult<T> {
  const ctx = useContext(StoresContext);
  const { state, persist } = getStoreFromContext(ctx, storeName, 'document');
  const typedState = state as DocumentState<T>;

  return {
    state: typedState,
    persist: persist as DocumentPersist<T>,
    useSnapshot,
  };
}
